/**
 * ExportFamilyButton — «Last ned familiens data» (FF-14, #182).
 *
 * En rad i Admin-lista, som «Tving oppdatering». Raden henter
 * `GET /api/admin/export` (krever fersk adminopplåsing) og lagrer zip-fila i
 * adminens egen nettleser: én JSON-fil per tabell og bevisbildene. Ingen
 * e-post (epic #167 D7), og ingen kopi hos systemadminen (D3).
 *
 * Fila hentes med fetch og ikke med en vanlig lenke, så et 403 (opplåsingen
 * har gått ut) blir en melding på raden og ikke en side med JSON.
 */
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../components/primitives';
import { ADMIN_ROW } from './adminStyles';

/** Filnavnet fra `Content-Disposition`, ellers et generisk navn. */
function filenameOf(res: Response): string {
  const match = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '');
  return match?.[1] ?? 'stell-eksport.zip';
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Gi nettleseren tid til å starte nedlastingen før URL-en slippes.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function ExportFamilyButton(): ReactElement {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onClick(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/export', { credentials: 'same-origin' });
      if (res.ok) saveBlob(await res.blob(), filenameOf(res));
      else if (res.status === 401 || res.status === 403) setError(t('admin.export.error_scope'));
      else setError(t('admin.export.error'));
    } catch {
      setError(t('error.offline'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="border-b border-card-line">
      <button
        type="button"
        onClick={() => void onClick()}
        disabled={pending}
        className={ADMIN_ROW}
        data-testid="export-family-trigger"
      >
        <span className="min-w-0 flex-1">
          <span className="block">{t('admin.export.cta')}</span>
          <span className="block text-caption font-medium text-ink-2">{t('admin.export.sub')}</span>
        </span>
        <span className="font-semibold text-ink-2">
          {pending ? t('admin.export.pending') : t('admin.export.value')}
        </span>
        <Icon.ChevR size={18} className="shrink-0 text-ink-2" />
      </button>
      {error !== null && (
        <p className="pb-3 text-sm text-bad" role="alert" data-testid="export-family-error">
          {error}
        </p>
      )}
    </div>
  );
}
